'use client';

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  loyaltyService,
  type ConfigureLoyaltyProgramBody,
  type LoyaltyAnalytics,
  type RecordLoyaltyEventBody,
} from '../services/loyalty';
import { customerProfileKeys } from './useCustomerProfiles';
import { createLogger } from '../utils/logger';

const logger = createLogger('useLoyalty');

/**
 * Query key factory for loyalty data (W4b). Scoped by restaurant.
 */
export const loyaltyKeys = {
  all: ['loyalty'] as const,
  scope: (tenantDocId: string, locationDocId: string) =>
    [...loyaltyKeys.all, tenantDocId, locationDocId] as const,
  program: (tenantDocId: string, locationDocId: string) =>
    [...loyaltyKeys.scope(tenantDocId, locationDocId), 'program'] as const,
  balance: (tenantDocId: string, locationDocId: string, customerUid: string) =>
    [...loyaltyKeys.scope(tenantDocId, locationDocId), 'balance', customerUid] as const,
  events: (tenantDocId: string, locationDocId: string, customerUid: string) =>
    [...loyaltyKeys.scope(tenantDocId, locationDocId), 'events', customerUid] as const,
  /**
   * Analytics key uses numeric IDs (same convention as salesReportKeys) since
   * the analytics endpoint lives under /reports/ and accepts tenantId/restaurantId.
   */
  analytics: (tenantId: number, locationNumericId: number, startDate: string, endDate: string) =>
    [...loyaltyKeys.all, 'analytics', tenantId, locationNumericId, startDate, endDate] as const,
};

export const useLoyaltyProgram = (
  tenantDocId?: string,
  locationDocId?: string,
) => {
  const enabled = !!tenantDocId && !!locationDocId;
  return useQuery({
    queryKey: enabled
      ? loyaltyKeys.program(tenantDocId!, locationDocId!)
      : loyaltyKeys.all,
    queryFn: () => loyaltyService.getProgram(tenantDocId!, locationDocId!),
    enabled,
    staleTime: 5 * 60_000,
  });
};

export const useConfigureLoyaltyProgram = (
  tenantDocId: string,
  locationDocId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ConfigureLoyaltyProgramBody) =>
      loyaltyService.configureProgram(tenantDocId, locationDocId, body),
    onSuccess: (program) => {
      queryClient.setQueryData(
        loyaltyKeys.program(tenantDocId, locationDocId),
        program,
      );
    },
    onError: (error) => {
      logger.error('Failed to configure loyalty program', error);
    },
  });
};

export const useLoyaltyBalance = (
  tenantDocId?: string,
  locationDocId?: string,
  customerUid?: string,
) => {
  const enabled = !!tenantDocId && !!locationDocId && !!customerUid;
  return useQuery({
    queryKey: enabled
      ? loyaltyKeys.balance(tenantDocId!, locationDocId!, customerUid!)
      : loyaltyKeys.all,
    queryFn: () => loyaltyService.getBalance(tenantDocId!, locationDocId!, customerUid!),
    enabled,
  });
};

export const useLoyaltyEvents = (
  tenantDocId?: string,
  locationDocId?: string,
  customerUid?: string,
  limit?: number,
) => {
  const enabled = !!tenantDocId && !!locationDocId && !!customerUid;
  return useQuery({
    queryKey: enabled
      ? [...loyaltyKeys.events(tenantDocId!, locationDocId!, customerUid!), { limit }]
      : loyaltyKeys.all,
    queryFn: () =>
      loyaltyService.listEvents(tenantDocId!, locationDocId!, customerUid!, limit),
    enabled,
  });
};

/**
 * Record a manual loyalty event (earn / burn / adjust). Caller must
 * supply an `idempotencyKey` — typically a client-generated UUID.
 * On success, both the balance and events queries are invalidated,
 * AND the customer profile detail/list queries (loyaltyBalance on
 * profile is the cached projection).
 */
export const useRecordLoyaltyEvent = (
  tenantDocId: string,
  locationDocId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordLoyaltyEventBody) =>
      loyaltyService.recordEvent(tenantDocId, locationDocId, body),
    onSuccess: (_, variables) => {
      logger.info('Loyalty event recorded', {
        customerUid: variables.customerUid,
        type: variables.type,
        points: variables.points,
      });
      queryClient.invalidateQueries({
        queryKey: loyaltyKeys.balance(tenantDocId, locationDocId, variables.customerUid),
      });
      queryClient.invalidateQueries({
        queryKey: loyaltyKeys.events(tenantDocId, locationDocId, variables.customerUid),
      });
      queryClient.invalidateQueries({
        queryKey: customerProfileKeys.detail(
          tenantDocId,
          locationDocId,
          variables.customerUid,
        ),
      });
      // Refresh the customers list so its loyaltyBalance projection re-fetches.
      queryClient.invalidateQueries({
        queryKey: customerProfileKeys.scope(tenantDocId, locationDocId),
      });
    },
    onError: (error) => {
      logger.error('Failed to record loyalty event', error);
    },
  });
};

/**
 * Loyalty program health analytics for the merchant dashboard.
 *
 * Uses NUMERIC tenant/restaurant IDs (same convention as useSalesReports) because
 * the endpoint lives under /api/v1/reports/ and requires tenantId/restaurantId.
 * startDate/endDate default to the last 30 days when not supplied.
 *
 * Returns member count, points earned/burned, outstanding liability, and
 * redemption rate for the chosen period. Results are stale after 5 min.
 */
export const useLoyaltyAnalytics = (
  tenantId?: number,
  locationNumericId?: number,
  startDate?: Date,
  endDate?: Date,
): UseQueryResult<LoyaltyAnalytics> => {
  const startStr = startDate?.toISOString() ?? '';
  const endStr   = endDate?.toISOString()   ?? '';
  const enabled  = !!tenantId && !!locationNumericId && !!startDate && !!endDate;
  return useQuery<LoyaltyAnalytics>({
    queryKey: enabled
      ? loyaltyKeys.analytics(tenantId!, locationNumericId!, startStr, endStr)
      : loyaltyKeys.all,
    queryFn: () =>
      loyaltyService.getAnalytics(tenantId!, locationNumericId!, startDate!, endDate!),
    enabled,
    staleTime: 5 * 60_000,
  });
};
